import type React from 'react'
import type {
  CMSButtonLink,
  CMSComponentResponsiveImage,
  CMSComponentVideo,
  CMSLinkUrl,
  CMSSectionTheme,
} from '@/types/sanity'
import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import { LinkButton } from '@/components/Link/LinkButton'
import {
  PortableText,
  type PortableTextReactComponents,
} from '@portabletext/react'
import styles from './InsetVariant.module.scss'
import { RichTextBlock } from '../SectionRichText/RichTextBlock'
import { Wrapper } from './FullBleedHeroBannerWrapper.tsx'

export type Device = 'desktop' | 'mobile'

export type InsetConfig = {
  bannerType: 'inset' | 'fullBleed' | 'legacy'
  insetContentPosition?: 'left' | 'right'
  eyebrow?: string
  eyebrowColor?: string
  header?: string
  description?: any
  cta?: CMSButtonLink
  theme?: CMSSectionTheme
  contentAlignment: 'left' | 'center'
  isModuleClickable?: boolean
  moduleUrl?: CMSLinkUrl
  content: {
    image?: CMSComponentResponsiveImage
    video?: CMSComponentVideo
  }
}

type InsetVariantProps = { config: InsetConfig }

const portableTextComponents: Partial<PortableTextReactComponents> = {
  block: RichTextBlock,
  types: {},
}

export const InsetVariant: React.FC<InsetVariantProps> = ({ config }) => {
  const {
    insetContentPosition = 'left',
    eyebrow,
    eyebrowColor,
    header,
    description,
    cta,
    content,
    contentAlignment,
    isModuleClickable,
    moduleUrl,
  } = config

  return (
    <Wrapper
      isModuleClickable={isModuleClickable}
      moduleUrl={moduleUrl}
      content={content}
    >
      <div className={styles.container} data-position={insetContentPosition}>
        <div className={styles.insetMedia}>
          {content?.video ? (
            <Video
              videoUrl={content.video.video.secureUrl}
              videoType={content.video.videoType}
              ratios={content.video.ratios}
              dialogueThumbnail={content.video.dialogue?.thumbnail}
              includeControls={content.video.dialogue?.includeControls}
              loopVideo={content.video.background?.loopVideo}
              captionsUrl={content.video.dialogue?.captionsUrl}
              backupImage={content.video.backupImage}
              classes={styles.video}
            />
          ) : content?.image ? (
            <ResponsiveImage document={content.image} />
          ) : null}
        </div>
        {eyebrow || header || description || (cta && cta.text) ? (
          <div
            className={styles.content}
            data-text={contentAlignment || 'left'}
          >
            <div className={styles.contentInner}>
              {eyebrow && (
                <p
                  className="type-label mb-16 md:mb-24"
                  style={{ color: eyebrowColor || undefined }}
                >
                  {eyebrow}
                </p>
              )}
              {header && (
                <h1
                  className="type-h1 md:type-h1 mb-32 mt-0"
                  dangerouslySetInnerHTML={{ __html: header! }}
                />
              )}
              {description && (
                <div
                  className="mb-32 md:mb-40"
                  style={{ '--text-align': contentAlignment } as any}
                >
                  <PortableText
                    value={description}
                    components={portableTextComponents}
                  />
                </div>
              )}
              {cta && cta.text && <LinkButton link={cta} className="mt-0" />}
            </div>
          </div>
        ) : null}
      </div>
    </Wrapper>
  )
}
